import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format, parseISO } from 'date-fns';

interface TrendPoint {
  local_date: string;
  pv_kwh: number;
  load_kwh: number;
  grid_import_kwh?: number;
  grid_export_kwh?: number;
}

interface EnergyTrendChartProps {
  data: TrendPoint[];
  title?: string;
  variant?: 'area' | 'bar';
}

export function EnergyTrendChart({ data, title = 'Energy Trend' }: EnergyTrendChartProps) {
  if (!data || data.length === 0) return null;

  const formatted = data.map((d) => ({
    ...d,
    date: format(parseISO(d.local_date), 'MMM d'),
  }));

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={formatted}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--solar-gold))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--solar-gold))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="loadGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--green-primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--green-primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              <XAxis
                dataKey="date"
                stroke="hsl(var(--muted-foreground))"
                fontSize={10}
                tickLine={false}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={10}
                tickFormatter={(v: number) => `${v.toFixed(0)}`}
                label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))', fontSize: 10 } }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(value: number) => [`${value.toFixed(1)} kWh`]}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))' }}
              />
              <Area
                type="monotone"
                dataKey="pv_kwh"
                name="Solar"
                stroke="hsl(var(--solar-gold))"
                strokeWidth={2}
                fill="url(#pvGrad)"
              />
              <Area
                type="monotone"
                dataKey="load_kwh"
                name="Usage"
                stroke="hsl(var(--green-primary))"
                strokeWidth={2}
                fill="url(#loadGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
